import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { buildQueryString } from "@mimber-client/utils"
import {
  OrderWithRelationsDto,
  ResponseDto,
  QueryParamsDto,
} from "@mimber/schema"
import { memberOrdersKeys } from "../member-orders.utils"

/**
 * Hook for fetching a member's orders
 * Can only be used in mimber-organization app
 *
 * @param memberUuid - Member UUID
 * @param params - Query parameters (pagination, sorting, filtering)
 * @param options - React Query options
 */
export const useMemberOrders = (
  memberUuid: string,
  params?: QueryParamsDto,
  options?: UseQueryOptions<ResponseDto<OrderWithRelationsDto[]>, Error>
): UseQueryResult<ResponseDto<OrderWithRelationsDto[]>, Error> => {
  guardAppUsage("mimber-organization", "useMemberOrders")

  const { server } = useServerContext()

  return useQuery({
    queryKey: memberOrdersKeys.getMemberOrders(memberUuid, params),
    queryFn: async () => {
      const response = await server.get<ResponseDto<OrderWithRelationsDto[]>>(
        buildQueryString(`members/${memberUuid}/orders`, params)
      )
      return response.data
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!memberUuid,
    ...options,
  })
}
